import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Bed, Bath, Square, Wifi, Zap, Droplet, Car, Shield, Home, Check, Phone, Mail, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { useApi } from '../context/ApiContext';
import { getSettings } from '../utils/settings';

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1600' height='1000' viewBox='0 0 1600 1000'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop stop-color='%23f97316'/%3E%3Cstop offset='1' stop-color='%23fed7aa'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='1600' height='1000' fill='url(%23g)'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='white' font-family='Arial,sans-serif' font-size='72' font-weight='700'%3ERaulfex Property%3C/text%3E%3C/svg%3E";

const PropertyDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { fetchProperty } = useApi();
  const settings = getSettings();
  const [property, setProperty] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Build an images array from gallery/heroImage even before render branches
  const images = useMemo(() => {
    if (!property) return [];
    const list = [];
    if (Array.isArray(property.gallery) && property.gallery.length) list.push(...property.gallery);
    if (property.heroImage) list.unshift(property.heroImage);
    return list.length ? list : [property.image, PLACEHOLDER_IMAGE].filter(Boolean);
  }, [property]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await fetchProperty(slug);
        if (active) setProperty(data || null);
      } catch (e) {
        if (active) setError(e?.message || 'Failed to load property');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [slug, fetchProperty]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-gray-600">Loading property...</div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Property Not Found</h2>
          <p className="text-gray-600 mb-4">{error || "The property you're looking for doesn't exist."}</p>
          <button
            onClick={() => navigate('/properties')}
            className="px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors"
          >
            Back to Properties
          </button>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    if (!images.length) return;
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    if (!images.length) return;
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const featureIcons = {
    wifi: <Wifi className="w-5 h-5" />,
    electricity: <Zap className="w-5 h-5" />,
    water: <Droplet className="w-5 h-5" />,
    parking: <Car className="w-5 h-5" />,
    security: <Shield className="w-5 h-5" />,
    garden: <Home className="w-5 h-5" />
  };

  const getFeaturesList = () => {
    // prefer backend amenities (string array); fallback to derived booleans
    if (Array.isArray(property.amenities) && property.amenities.length) {
      return property.amenities.map((name) => ({ name }));
    }
    return [];
  };

  const availableUnits = Array.isArray(property.units)
    ? property.units.filter((unit) => unit.available)
    : [];

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12 mt-[25px]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Back Button */}
        <button
          onClick={() => navigate('/properties')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Properties</span>
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Main Content */}
          <div className="lg:col-span-2">
            
            {/* Image Gallery */}
            <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg mb-6">
              <img
                src={images[currentImageIndex]}
                alt={`${property.name} ${currentImageIndex + 1}`}
                className="w-full h-64 sm:h-96 lg:h-[500px] object-cover"
              />
              
              {/* Image Navigation */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all"
                  >
                    <ChevronLeft className="w-6 h-6 text-gray-900" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all"
                  >
                    <ChevronRight className="w-6 h-6 text-gray-900" />
                  </button>
                  
                  {/* Image Indicators */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentImageIndex ? 'bg-white w-8' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
              
              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${
                  property.category === 'Residential' ? 'bg-orange-500 text-white' : 
                  property.category === 'Commercial' ? 'bg-blue-500 text-white' :
                  'bg-gray-700 text-white'
                }`}>
                  {property.category}
                </span>
                {property.status === 'Rent' && (
                  <span className="bg-white/90 backdrop-blur-sm text-gray-900 text-xs font-semibold px-3 py-1.5 rounded-full">
                    For {property.status}
                  </span>
                )}
              </div>
            </div>

            {/* Property Details */}
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">{property.name}</h1>
                  {property.headline && (
                    <p className="text-base sm:text-lg text-gray-600 mb-3 max-w-2xl">{property.headline}</p>
                  )}
                  <div className="flex items-center text-gray-600">
                    <MapPin className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span className="text-lg">{property.address}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl sm:text-4xl font-bold text-orange-500">
                    {property.price != null ? `R${Number(property.price).toLocaleString()}` : ''}
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              {property.category === 'Residential' ? (
                <div className="grid grid-cols-3 gap-4 py-6 border-y border-gray-200">
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Bed className="w-6 h-6 text-orange-500" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{property.beds}</div>
                    <div className="text-sm text-gray-600">Bedrooms</div>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Bath className="w-6 h-6 text-orange-500" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{property.baths}</div>
                    <div className="text-sm text-gray-600">Bathrooms</div>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Square className="w-6 h-6 text-orange-500" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{property.sqft}</div>
                    <div className="text-sm text-gray-600">Sqft</div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 py-6 border-y border-gray-200">
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Square className="w-6 h-6 text-orange-500" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{property.sqft}</div>
                    <div className="text-sm text-gray-600">Square Feet</div>
                  </div>
                  {property.parking && (
                    <div className="text-center">
                      <div className="flex items-center justify-center mb-2">
                        <Car className="w-6 h-6 text-orange-500" />
                      </div>
                      <div className="text-2xl font-bold text-gray-900">{property.parking}</div>
                      <div className="text-sm text-gray-600">Parking Bays</div>
                    </div>
                  )}
                </div>
              )}

              {/* Description */}
              <div className="mt-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Description</h2>
                <p className="text-gray-700 leading-relaxed">{property.description}</p>
              </div>

              {/* Property Info */}
              <div className="mt-6 grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                {property.yearBuilt && (
                  <div>
                    <div className="text-sm text-gray-600">Year Built</div>
                    <div className="font-semibold text-gray-900">{property.yearBuilt}</div>
                  </div>
                )}
                <div>
                  <div className="text-sm text-gray-600">Property Type</div>
                  <div className="font-semibold text-gray-900">{property.category}</div>
                </div>
              </div>
            </div>

            {/* Features & Amenities */}
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Features & Amenities</h2>
              {getFeaturesList().length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {getFeaturesList().map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 flex-shrink-0">
                        {feature.icon && featureIcons[feature.icon] ? featureIcons[feature.icon] : <Check className="w-5 h-5" />}
                      </div>
                      <span className="text-gray-900 font-medium">{feature.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">Amenities are being finalized for this property. Contact the leasing team for the latest details.</p>
              )}
            </div>

            {Array.isArray(property.units) && property.units.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mt-6">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Available Layouts</h2>
                    <p className="text-gray-600">
                      {availableUnits.length > 0
                        ? `${availableUnits.length} unit${availableUnits.length === 1 ? '' : 's'} ready to apply for`
                        : 'No units are marked vacant right now, but the leasing team can help with timing.'}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {property.units.map((unit) => (
                    <div key={unit.id} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div>
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{unit.name || unit.code}</h3>
                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${unit.available ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                              {unit.available ? 'Available' : unit.status}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                            <span>{unit.beds} bed</span>
                            <span>{unit.baths} bath</span>
                            <span>{unit.sqft} sqft</span>
                            {unit.availableFrom && <span>Available from {unit.availableFrom}</span>}
                          </div>
                        </div>

                        <div className="md:text-right">
                          <div className="text-2xl font-bold text-orange-500 mb-2">
                            {unit.price != null ? `R${Number(unit.price).toLocaleString()}` : ''}
                          </div>
                          {settings.allowApplyFromProperties && property.status === 'Rent' && unit.available && (
                            <button
                              onClick={() => navigate(`/apply-to-rent?property=${encodeURIComponent(slug)}&unit=${encodeURIComponent(unit.id)}`)}
                              className="px-4 py-2 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors"
                            >
                              Apply for this unit
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            
            {/* Agent Contact Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Contact Agent</h3>
              
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-200">
                {property.agentName && (
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                    {property.agentName.split(' ').map(n => n[0]).join('')}
                  </div>
                )}
                <div>
                  <div className="font-bold text-gray-900 text-lg">{property.agentName || 'Agent'}</div>
                  <div className="text-sm text-gray-600">Property Agent</div>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                {property.agentPhone && (
                  <a
                    href={`tel:${property.agentPhone}`}
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Phone className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span className="text-gray-900 font-medium">{property.agentPhone}</span>
                  </a>
                )}
                {property.agentEmail && (
                  <a
                    href={`mailto:${property.agentEmail}`}
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Mail className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span className="text-gray-900 font-medium break-all">{property.agentEmail}</span>
                  </a>
                )}
              </div>

              {settings.allowApplyFromProperties && property.status === 'Rent' && (
                <button
                  onClick={() => navigate(`/apply-to-rent?property=${encodeURIComponent(slug)}`)}
                  className="w-full px-6 py-4 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors shadow-lg hover:shadow-xl mb-3"
                >
                  Apply to Rent
                </button>
              )}

              <button className="w-full px-6 py-4 bg-white border-2 border-gray-900 text-gray-900 font-bold rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
                Schedule Viewing
              </button>
              <button className="w-full mt-3 px-6 py-4 border-2 border-gray-900 text-gray-900 font-bold rounded-lg hover:bg-gray-900 hover:text-white transition-all">
                Send Message
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
